/**
 * "Invia contatto": cerca tra Lead/Clienti e referenti (client_contacts) della propria
 * organizzazione e invia nome + telefono come scheda contatto (vCard) nella chat.
 * L'invio usa l'azione dello store (sendContactMessage), valida per tutti i numeri.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, User, Building2, Loader2, Send } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { iconTip } from '@/components/messages/ChatTooltip';
import { useUserData } from '@/lib/auth';
import { searchClientsForChat } from '@/lib/services/whatsapp/clientPhoneSearch';

interface Option { key: string; name: string; phone: string; subtitle: string; organization?: string; isPerson: boolean }

interface ContactPickerModalProps { onClose: () => void }

export function ContactPickerModal({ onClose }: ContactPickerModalProps) {
  const orgId = useWhatsAppChatStore((s) => s.wahaConfig?.organizationId);
  const sendContactMessage = useWhatsAppChatStore((s) => s.sendContactMessage);
  const { userProfile } = useUserData();
  const userId = userProfile?.id ?? null;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const q = query.trim().replace(/[%,()]/g, ' ').trim();
    if (!orgId || q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      const like = `%${q}%`;
      // Lead/Clienti con la stessa ricerca di "Nuova chat": rispetta le restrizioni di
      // visibilita' del ruolo (solo i miei, pipeline, fonte) e distingue Lead da Cliente.
      const [clients, contactsRes] = await Promise.all([
        searchClientsForChat(orgId, userId, q).catch((err) => {
          console.error('[ContactPicker] ricerca Lead/Clienti non riuscita:', err);
          return [];
        }),
        supabase.from('client_contacts')
          .select('id, name, phone, role, client:clients(business_name)')
          .eq('organization_id', orgId)
          .eq('is_active', true)
          .or(`name.ilike.${like},phone.ilike.${like}`)
          .limit(8),
      ]);
      if (cancelled) return;
      const opts: Option[] = [];
      for (const c of clients) {
        const phone = c.numbers[0];
        if (!phone) continue;
        opts.push({
          key: `c-${c.id}`,
          name: c.contactName || c.name,
          phone,
          subtitle: `${c.kind}${c.contactName ? ` · ${c.name}` : ''}`,
          organization: c.contactName ? c.name : undefined,
          isPerson: !!c.contactName,
        });
      }
      for (const p of contactsRes.data || []) {
        if (!p.phone) continue;
        const company = (p.client as { business_name?: string } | null)?.business_name;
        opts.push({
          key: `p-${p.id}`,
          name: p.name,
          phone: p.phone,
          subtitle: ['Referente', p.role, company].filter(Boolean).join(' · '),
          organization: company,
          isPerson: true,
        });
      }
      setResults(opts);
      setLoading(false);
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, orgId, userId]);

  const handlePick = async (o: Option) => {
    setSendingKey(o.key);
    try {
      await sendContactMessage({ name: o.name, phone: o.phone });
      toast.success(`Contatto di ${o.name} inviato`);
      onClose();
    } catch (err) {
      console.error('[ContactPicker] send error:', err);
      toast.error('Invio del contatto non riuscito. Riprova tra poco.');
      setSendingKey(null);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Invia contatto">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <User className="w-5 h-5 text-[#787774]" />
          <h2 className="flex-1 text-sm font-semibold text-[#37352F] dark:text-white">Invia un contatto</h2>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10" {...iconTip('Chiudi')}>
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded-lg px-3 py-2">
            <Search className="w-4 h-4 text-[#787774]" />
            <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, azienda o telefono..."
              className="flex-1 text-sm bg-transparent outline-none text-[#37352F] dark:text-white placeholder:text-[#787774]" />
            {loading && <Loader2 className="w-4 h-4 animate-spin text-[#787774]" />}
          </div>
          <div className="mt-3 max-h-72 overflow-y-auto">
            {query.trim().length < 2 && <p className="text-xs text-[#787774] text-center py-6">Scrivi almeno 2 lettere. Compaiono solo Lead, Clienti e referenti con un numero di telefono.</p>}
            {query.trim().length >= 2 && !loading && results.length === 0 && (
              <p className="text-xs text-[#787774] text-center py-6">Nessun risultato con numero di telefono</p>
            )}
            {results.map((o) => (
              <button key={o.key} type="button" onClick={() => handlePick(o)} disabled={!!sendingKey}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 disabled:opacity-50">
                {o.isPerson ? <User className="w-4 h-4 text-[#787774] flex-shrink-0" /> : <Building2 className="w-4 h-4 text-[#787774] flex-shrink-0" />}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-[#37352F] dark:text-white truncate">{o.name}</span>
                  <span className="block text-[11px] text-[#787774] truncate">{o.phone} · {o.subtitle}</span>
                </span>
                {sendingKey === o.key ? <Loader2 className="w-4 h-4 animate-spin text-[#2383E2]" /> : <Send className="w-3.5 h-3.5 text-[#A4A4A0]" />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
